import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button} from 'antd';
import DefaultPage from '../../layout/common/DefaultPage';
import ModelUploadHeaderRedesigned from './ModelUploadHeaderRedesigned';
import StepFiles from './StepFiles/StepFiles';
import StepMetadata from './StepMetada/StepMetadata';
import StepLineage from './StepLineage/StepLineage';
import StepPermissions from './StepPermissions/StepPermissions';
import StepReviewRedesigned from './StepReview/StepReviewRedesigned';
import {
  modelFileMarkChangelog,
  modelFileSelector,
  modelFileUploaderClear,
  ModelTreeNode,
  useModelFileUploader,
} from '../../../store/modelFile';
import {blankModel, MODEL_UPLOAD_STEPS, ModelUploadFlow} from './types';
import {ModelEditPermissionsFormData} from '../model/common/ModelEdit/ModelEditPermissons/ModelEditPermissionsForm';
import {useCurrentUser} from '../../../store/auth';
import {ModelDTO, UserType} from 'hemwb-api';
import DialogProgress from '../../core/dialog/DialogProgress';
import {useDispatch, useStore} from 'react-redux';
import {
  getModelSteward,
  isModelPublished,
  isStaticMetadataValid,
  isModelWeb,
  modelCreate,
  modelLoadDetail,
  modelPublish,
  modelUpdate,
  modifyModelMetadata,
  useModelDetail,
  useModels,
} from '../../../store/model';
import {requestUpdateAiSummaryPreference} from '../../../store/model/api';
import {trackModelCreated, trackModelPublished} from '../../../tracking/tracking';
import {gridStateSet, modelListGridId} from '../../../store/grid';
import {getUrlRoute, MRAppRoutes, useRoute} from '../../router/routes';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMounted} from '../../../hooks/useMounted';
import {messageAdd, MessageTypes} from '../../../store/message';
import {useVisited} from '../../../hooks/useVisited';
import SuspenseNull from '../../core/Suspense/SuspenseNull';
import {mapValuesToModel, transformModelToValues} from '../model/common/ModelEdit/ModelEditMetadata/utils';
import {modelLinksSetParent} from '../../../store/modelLinks';
import {ModelUploadContext} from './ModelUploadContext';
import {StepStatus} from '../../core/materialui/Stepper';
import {getStepStatus} from './utils';
import {validateModel} from '../../../store/model/validator';
import DialogNavigationIntercepted, {SetEnabledInterceptor} from '../../core/dialog/DialogNavigationIntercepted';
import {modelStewardSet} from '../../../store/modelSteward';
import {modelAccessUpdateList} from '../../../store/modelAccess';
import NotificationConfirmationDialogAntd from '../model/common/ModelEdit/ModelEditFiles/NotificationConfirmationDialogAntd';
import {modelGenerateName} from '../../../store/model/modelGenerateName';
import {userExpertiseLoadOptionsSet} from '../../../store/userExpertise';
import {hardCodedOptions} from '../model/common/ModelEdit/ModelEditMetadata/constants';
import {modelMetadataLoadOptions} from '../../../store/modelMetadata';
import {useModelAccess} from '../../../store/modelAccess';
import {useModelFile} from '../../../store/modelFile';
import {RootState} from '../../../store/rootReducer';
import {userFindValueReturned} from '../../../store/user/utils';

const ModelUpload: React.FC = () => {
  const {uuid} = useRoute<MRAppRoutes.MODEL_UPLOAD>();
  const {search} = useLocation();
  const detail = useModelDetail(uuid);
  const models = useModels();
  const {files: loadedFiles} = useModelFile(detail || null);
  const {
    loaded: loadedAccess,
    owners,
    viewers,
    displayViewers,
    calibrationModelers,
    scenarioModelers,
    vestLead,
  } = useModelAccess(uuid || '');

  const query = useMemo(() => new URLSearchParams(search), [search]);
  const editMode = query.get('editMode') === 'true';
  const editStep = query.get('editStep');
  const editStepIndex = useMemo(() => {
    switch (editStep) {
      case 'files':
        return MODEL_UPLOAD_STEPS.FILES;
      case 'permissions':
        return MODEL_UPLOAD_STEPS.PERMISSIONS;
      case 'metadata':
      default:
        return MODEL_UPLOAD_STEPS.METADATA;
    }
  }, [editStep]);

  const [activeStep, setActiveStep] = useState(editMode ? editStepIndex : MODEL_UPLOAD_STEPS.FILES);
  const [flow] = useState(!!uuid ? ModelUploadFlow.BASED_ON_METADATA : ModelUploadFlow.MANUAL_ENTRY);

  // HEMT-8938: track whether user chose to opt out of AI summary during creation
  // ref is used in save() to avoid stale closure — state drives the UI re-render
  const [aiSummaryOptedOut, setAiSummaryOptedOutState] = useState(false);
  const aiSummaryOptedOutRef = useRef(false);
  const setAiSummaryOptedOut = useCallback((value: boolean) => {
    aiSummaryOptedOutRef.current = value;
    setAiSummaryOptedOutState(value);
  }, []);

  const [initialized, setInitialized] = useState(!uuid);
  const [showErrorsOnStep, setShowErrorsOnStep] = useState<number | undefined>();
  const [isUpdatingFiles, setIsUpdatingFiles] = useState(false);
  const wasVisited = useVisited(activeStep);
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mounted = useMounted();
  const setEnabledInterceptorRef = useRef<SetEnabledInterceptor>();
  const scrollRef = useRef<HTMLElement | null>(null);

  const [submitText, setSubmitText] = useState('');
  const [files, setFilesState] = useState<ModelTreeNode[]>([]);
  const [model, setModelState] = useState<ModelDTO>(
    currentUser.userType === UserType.EXTERNALUSER
      ? blankModel
      : modifyModelMetadata(blankModel, {
          isid_of_model_steward: currentUser.id || '',
          email_for_model_steward: currentUser.email || '',
          name_of_model_steward:
            currentUser.firstName && currentUser.lastName ? `${currentUser.lastName}, ${currentUser.firstName}` : '',
        }),
  );
  const [suffixValidity, _setSuffixValidity] = useState(false);
  const [permissions, setPermissions] = useState<
    Pick<
      ModelEditPermissionsFormData,
      'owners' | 'viewers' | 'displayViewers' | 'calibrationModelers' | 'scenarioModelers' | 'vestLead'
    >
  >({
    owners: [],
    calibrationModelers: [],
    scenarioModelers: [],
    viewers: [],
    displayViewers: [],
    vestLead: undefined,
  });
  const [permissionsInitializedFromModel, setPermissionsInitializedFromModel] = useState(false);
  const [permissionsAutoPopulatedFromVvmc, setPermissionsAutoPopulatedFromVvmc] = useState(false);
  const [revopsName, setRevopsName] = useState<string>('');

  const issues = useMemo(() => {
    if (models) {
      return validateModel(model, models, files, permissions.owners, permissions.vestLead);
    }
    return [];
  }, [model, models, files, permissions]);

  // In edit mode, don't pass an empty array — that would pre-initialize the uploader with no nodes
  // before the loaded files arrive, preventing FileManagerAntd from ever re-initializing.
  // Pass undefined so the hook waits for files via its internal useModelFile loader.
  const {
    start: saveFiles,
    remainingOperationsCount,
    totalOperationsCount,
  } = useModelFileUploader(model, editMode && files.length === 0 ? undefined : files);
  const store = useStore<RootState>();
  const [openNotifyDialog, setOpenNotifyDialog] = useState(false);

  const setModel = useCallback((newModel: ModelDTO) => {
    setModelState({...newModel, name: modelGenerateName(newModel)});
  }, []);

  const contextValue = useMemo(() => ({flow, issues, model, onModelChange: setModel}), [flow, issues, model, setModel]);

  const setFiles = useCallback((fs: ModelTreeNode[]) => {
    setFilesState(fs);
  }, []);

  useEffect(() => {
    if (files.length) {
      setEnabledInterceptorRef.current?.(true);
    }
  }, [files]);

  useEffect(() => {
    if (activeStep !== MODEL_UPLOAD_STEPS.FILES) {
      setEnabledInterceptorRef.current?.(true);
    }
  }, [activeStep]);

  useEffect(() => {
    if (editMode) {
      setActiveStep(editStepIndex);
    }
  }, [editMode, editStepIndex]);

  useEffect(() => {
    if (editMode && loadedFiles && files.length === 0) {
      setFiles(loadedFiles);
    }
  }, [editMode, loadedFiles, files.length, setFiles]);

  useEffect(() => {
    if (editMode && loadedAccess && !permissionsInitializedFromModel) {
      setPermissions({
        owners: (owners as any) || [currentUser],
        viewers: (viewers as any) || [],
        displayViewers: (displayViewers as any) || [],
        calibrationModelers: (calibrationModelers as any) || [],
        scenarioModelers: (scenarioModelers as any) || [],
        vestLead: vestLead as any,
      });
      setPermissionsInitializedFromModel(true);
    }
  }, [
    editMode,
    loadedAccess,
    owners,
    viewers,
    displayViewers,
    calibrationModelers,
    scenarioModelers,
    vestLead,
    permissionsInitializedFromModel,
    currentUser,
  ]);

  useEffect(() => {
    if (detail) {
      let newModel: ModelDTO;
      if (editMode) {
        newModel = {
          ...detail,
          metadata: {...detail.metadata},
        };
      } else {
        const metadata = {...detail.metadata};
        delete metadata['version_log'];
        newModel = mapValuesToModel(transformModelToValues({...detail, metadata}), blankModel);
        newModel.metadata.tree_root_id = detail.metadata.tree_root_id;
        if (isModelPublished(detail)) {
          newModel.metadata.tree_parent_id = detail.uuid;
        }
      }
      setModel(newModel);
      setInitialized(true);
    }
  }, [detail, setModel, editMode]);

  // Track if autofill attempt is already in progress to prevent multiple concurrent runs
  const autofillInProgressRef = useRef(false);

  useEffect(() => {
    if (editMode || permissionsAutoPopulatedFromVvmc) {
      return;
    }

    // Skip if autofill is already in progress (prevents re-running when model changes during autofill)
    if (autofillInProgressRef.current) {
      return;
    }

    const ownerHint = model.metadata?.owner?.trim();
    const vaiLeadHint = model.metadata?.vai_lead?.trim();
    const hedsLeadHint = model.metadata?.heds_lead?.trim();

    // Run only when VVMC has provided at least one person hint and permissions are not user-filled yet.
    if (!ownerHint && !vaiLeadHint && !hedsLeadHint) {
      return;
    }
    // In create mode, owners starts as empty []. In edit mode, it's populated from the model.
    // Check if permissions are still in the "default" state (empty or single currentUser entry)
    const hasDefaultOwner =
      permissions.owners.length === 0 ||
      (permissions.owners.length === 1 && permissions.owners[0]?.id === currentUser.id);
    const hasDefaultVestLead = !permissions.vestLead || permissions.vestLead?.id === currentUser.id;
    const canReplaceDefaultPermissions = hasDefaultOwner && hasDefaultVestLead;

    if ((permissions.owners.length > 0 || permissions.vestLead) && !canReplaceDefaultPermissions) {
      setPermissionsAutoPopulatedFromVvmc(true);
      return;
    }

    let cancelled = false;
    autofillInProgressRef.current = true;

    const resolveUser = async (hint?: string) => {
      if (!hint) {
        return null;
      }
      const users = await userFindValueReturned(dispatch, hint, {
        limit: 10,
        includeNpa: true,
        includeUnenrolled: true,
      });
      const userList = users || []; // Handle undefined response
      const normalized = hint.toLowerCase();
      const hintLocalPart = normalized.split('@')[0];
      const normalizePrincipal = (value?: string) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      const exactMatch = userList.find(
        (u) => u.id?.toLowerCase() === normalized || u.email?.toLowerCase() === normalized,
      );
      if (exactMatch) {
        return exactMatch;
      }

      const localPartMatch = userList.find((u) => {
        const id = u.id?.toLowerCase() || '';
        const email = u.email?.toLowerCase() || '';
        const emailLocalPart = email.split('@')[0] || '';
        return (
          id === hintLocalPart ||
          emailLocalPart === hintLocalPart ||
          normalizePrincipal(id) === normalizePrincipal(hintLocalPart) ||
          normalizePrincipal(emailLocalPart) === normalizePrincipal(hintLocalPart)
        );
      });
      if (localPartMatch) {
        return localPartMatch;
      }

      const hintTokens = hintLocalPart.split(/[._-]+/).filter(Boolean);
      const tokenScoredCandidates = userList
        .map((u) => {
          const id = (u.id || '').toLowerCase();
          const emailLocalPart = (u.email || '').toLowerCase().split('@')[0] || '';
          const haystack = `${id} ${emailLocalPart}`;
          const score = hintTokens.reduce((acc, token) => (haystack.includes(token) ? acc + 1 : acc), 0);
          return {u, score};
        })
        .filter((candidate) => candidate.score > 0)
        .sort((left, right) => right.score - left.score);

      if (tokenScoredCandidates.length === 1) {
        return tokenScoredCandidates[0].u;
      }
      if (tokenScoredCandidates.length > 1 && tokenScoredCandidates[0].score > tokenScoredCandidates[1].score) {
        return tokenScoredCandidates[0].u;
      }

      if (userList.length === 1) {
        return userList[0];
      }

      // Fallback for VVMC owner hints that are email values not resolvable in directory search (e.g., @msd.com aliases).
      if (hint.includes('@')) {
        const localPart = hintLocalPart.trim();
        if (localPart) {
          return {
            id: localPart,
            email: hint,
          } as any;
        }
      }

      return null;
    };

    const toStewardName = (u: {firstName?: string; lastName?: string}) => {
      const parts = [u.lastName, u.firstName].filter(Boolean);
      return parts.join(', ');
    };

    const autoPopulate = async () => {
      try {
        const [ownerUser, vestLeadUser, stewardUser] = await Promise.all([
          resolveUser(ownerHint),
          resolveUser(vaiLeadHint),
          resolveUser(hedsLeadHint),
        ]);

        if (cancelled) {
          return;
        }

        const resolvedOwner = ownerUser || vestLeadUser || stewardUser;
        const resolvedVestLead = vestLeadUser || ownerUser || stewardUser;
        const resolvedSteward = stewardUser || ownerUser || vestLeadUser;

        if (resolvedOwner || resolvedVestLead) {
          setPermissions((previous) => {
            const newOwners = resolvedOwner ? [resolvedOwner] : previous.owners;
            const newVestLead = resolvedVestLead || previous.vestLead;
            return {
              ...previous,
              owners:
                previous.owners.length === 0 ||
                (previous.owners.length === 1 && previous.owners[0]?.id === currentUser.id)
                  ? newOwners
                  : previous.owners,
              vestLead:
                !previous.vestLead || previous.vestLead?.id === currentUser.id ? newVestLead : previous.vestLead,
            };
          });
        }

        if (resolvedSteward) {
          const currentSteward = getModelSteward(model);
          const shouldReplaceSteward = !currentSteward || currentSteward.id === currentUser.id;
          if (shouldReplaceSteward) {
            setModel(
              modifyModelMetadata(model, {
                isid_of_model_steward: resolvedSteward.id || '',
                email_for_model_steward: resolvedSteward.email || '',
                name_of_model_steward: toStewardName(resolvedSteward),
              }),
            );
          }
        }
      } finally {
        if (!cancelled) {
          setPermissionsAutoPopulatedFromVvmc(true);
        }
        autofillInProgressRef.current = false;
      }
    };

    autoPopulate();

    return () => {
      cancelled = true;
    };
  }, [
    editMode,
    permissionsAutoPopulatedFromVvmc,
    model.metadata?.owner,
    model.metadata?.vai_lead,
    model.metadata?.heds_lead,
    model.metadata?.isid_of_model_steward,
    currentUser.id,
    dispatch,
    setModel,
  ]);

  useEffect(() => {
    return () => {
      dispatch(modelFileUploaderClear(''));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isUpdatingFiles) {
      return;
    }

    if (totalOperationsCount <= 0) {
      setSubmitText('Uploading files');
      return;
    }

    const currentFileIndex = Math.min(
      totalOperationsCount,
      Math.max(1, totalOperationsCount - remainingOperationsCount + 1),
    );
    const currentFileLabel = currentFileIndex === 1 ? 'file' : 'files';
    const totalFileLabel = totalOperationsCount === 1 ? 'file' : 'files';

    setSubmitText(`Uploading ${currentFileIndex} ${currentFileLabel} out of ${totalOperationsCount} ${totalFileLabel}`);
  }, [isUpdatingFiles, remainingOperationsCount, totalOperationsCount]);

  const _setActiveStepAndShowErrors = useCallback((index: number) => {
    setShowErrorsOnStep(index);
    setActiveStep(index);
  }, []);

  const save = useCallback(
    async (updatedModel: ModelDTO, publish: boolean) => {
      try {
        setSubmitText('Creating model entry');
        const createdModel = await modelCreate(dispatch, {
          metadata: updatedModel.metadata,
        });
        const {uuid} = createdModel;
        dispatch(gridStateSet(modelListGridId, {expandedRowId: uuid}));

        try {
          // TODO: Implement user expertise loading
          const expertiseLevel = hardCodedOptions['expertiseLevel']?.[3];
          await userExpertiseLoadOptionsSet(dispatch, uuid, false, {
            userId: permissions.owners[0].id,
            expertiseTypeLevel: expertiseLevel ? expertiseLevel : '',
            subModelIdList: [],
          });
        } catch (e) {
          dispatch(messageAdd('Failed to save expertise level', MessageTypes.WARNING));
        }
        const {tree_parent_id} = updatedModel.metadata;
        if (tree_parent_id) {
          setSubmitText('Saving lineage');
          await modelLinksSetParent(dispatch, uuid, tree_parent_id, true);
        }

        setSubmitText('Saving permissions');

        const modelSteward = getModelSteward(updatedModel);
        if (modelSteward) {
          await modelStewardSet(dispatch, uuid, modelSteward);
        }

        await modelAccessUpdateList(dispatch, uuid, {
          owners: permissions.owners,
          vestLead: permissions.vestLead,
          calibratorModeler: permissions.calibrationModelers,
          scenarioModeler: permissions.scenarioModelers,
          viewers: permissions.viewers,
          displayViewers: permissions.displayViewers,
        });

        setIsUpdatingFiles(true);
        try {
          await saveFiles({...updatedModel, uuid});
        } catch (e) {
          dispatch(messageAdd('Failed to save files', MessageTypes.WARNING));
        } finally {
          setIsUpdatingFiles(false);
        }
        // To Update the store with newly created fileTags
        modelMetadataLoadOptions(dispatch);
        //SET CHANGELOG FILE
        if (model.metadata.changelog_file) {
          //need to recalculate itemUuid
          const originalNode = files.find((n) => n.uuid === model.metadata.changelog_file);
          if (originalNode) {
            const newNode = modelFileSelector(store.getState()).uploader.nodes.find(
              (n) => n.path === originalNode.path,
            );
            if (newNode) {
              setSubmitText('Saving changelog file');
              await modelFileMarkChangelog(dispatch, uuid, newNode.uuid);
            }
          }
        }

        if (publish) {
          await modelPublish(dispatch, uuid);
          trackModelPublished(createdModel);
          dispatch(messageAdd('Model created and published', MessageTypes.SUCCESS));
        } else {
          trackModelCreated(createdModel);
          dispatch(messageAdd('Model created', MessageTypes.SUCCESS));
        }

        // HEMT-8938: persist opt-out preference if user chose to opt out during model creation
        if (aiSummaryOptedOutRef.current) {
          try {
            await requestUpdateAiSummaryPreference(uuid, true);
          } catch (e) {
            dispatch(
              messageAdd(
                'AI summary preference could not be saved. You can update it from the model detail page.',
                MessageTypes.WARNING,
              ),
            );
          }
        }

        setEnabledInterceptorRef.current?.(false);
        navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid}));
      } catch (e) {
        mounted.current && setSubmitText('');
      }
    },
    [dispatch, navigate, mounted, model, permissions, saveFiles, files, store],
  );

  const getStepStatusCb = useCallback(
    (step: number): StepStatus => {
      return getStepStatus(step, wasVisited(step), issues, suffixValidity);
    },
    [wasVisited, issues, suffixValidity],
  );

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 0);
  }, [activeStep, scrollRef]);

  const handleEditCancel = useCallback(() => {
    if (uuid) {
      navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid}));
    } else {
      navigate(getUrlRoute(MRAppRoutes.MODEL_LIST));
    }
  }, [navigate, uuid]);

  const handleEditMetadataSubmit = useCallback(
    async (updatedModel: ModelDTO) => {
      if (!uuid) {
        return;
      }
      try {
        setSubmitText('Updating metadata');
        await modelUpdate(dispatch, uuid, updatedModel);

        const existingParentId = detail?.metadata?.tree_parent_id || '';
        const updatedParentId = updatedModel.metadata?.tree_parent_id || '';
        if (updatedParentId && updatedParentId !== existingParentId) {
          setSubmitText('Updating lineage');
          await modelLinksSetParent(dispatch, uuid, updatedParentId, true);
        }

        dispatch(messageAdd('Metadata updated', MessageTypes.SUCCESS));
        setEnabledInterceptorRef.current?.(false);
        navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid}));
      } finally {
        mounted.current && setSubmitText('');
      }
    },
    [uuid, dispatch, navigate, detail?.metadata?.tree_parent_id, mounted],
  );

  const handleStewardReset = useCallback(() => {
    // Update model metadata to set current user as steward (BARDS HEDS Lead)
    const stewardName = [currentUser.lastName, currentUser.firstName].filter(Boolean).join(', ');

    setModel(
      modifyModelMetadata(model, {
        isid_of_model_steward: currentUser.id || '',
        email_for_model_steward: currentUser.email || '',
        name_of_model_steward: stewardName,
      }),
    );

    // Only clear permissions - metadata clearing is handled by the metadata form
    setPermissions({
      owners: [],
      viewers: [],
      displayViewers: [],
      calibrationModelers: [],
      scenarioModelers: [],
      vestLead: undefined,
    });

    // Notify user that permissions were cleared
    dispatch(messageAdd('Permissions cleared - RevOps ID was cleared', MessageTypes.INFO));
  }, [dispatch]);

  const handleEditPermissionsSubmit = useCallback(
    async ({model: permissionsModel, ...newPermissions}: ModelEditPermissionsFormData) => {
      if (!uuid) {
        return;
      }
      try {
        setSubmitText('Updating permissions');
        await modelAccessUpdateList(dispatch, uuid, {
          owners: newPermissions.owners,
          vestLead: newPermissions.vestLead,
          calibratorModeler: newPermissions.calibrationModelers,
          scenarioModeler: newPermissions.scenarioModelers,
          viewers: newPermissions.viewers,
          displayViewers: newPermissions.displayViewers,
        });

        const modelSteward = getModelSteward(permissionsModel);
        if (modelSteward) {
          await modelStewardSet(dispatch, uuid, modelSteward);
        }

        dispatch(messageAdd('Permissions updated', MessageTypes.SUCCESS));
        // After permission update, reload model detail to get fresh data with updated timestamp
        // Note: modelAccessUpdateList returns 204 (empty), so we must explicitly fetch the model
        modelLoadDetail.clear(dispatch, uuid);
        await modelLoadDetail(dispatch, uuid);
        // Wait for Redux state to stabilize after update
        await new Promise((resolve) => setTimeout(resolve, 150));

        setEnabledInterceptorRef.current?.(false);
        navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid}));
      } finally {
        mounted.current && setSubmitText('');
      }
    },
    [uuid, dispatch, navigate, mounted],
  );

  const handleEditFilesSubmit = useCallback(
    async (updatedModel: ModelDTO, updatedFiles: ModelTreeNode[]) => {
      if (!uuid) {
        return;
      }
      try {
        setModel(updatedModel);
        setFiles(updatedFiles);
        setIsUpdatingFiles(true);
        await saveFiles(updatedModel);
        modelMetadataLoadOptions(dispatch);
        dispatch(messageAdd('Files have been updated', MessageTypes.SUCCESS));
        setEnabledInterceptorRef.current?.(false);
        setOpenNotifyDialog(true);
      } finally {
        setIsUpdatingFiles(false);
        mounted.current && setSubmitText('');
      }
    },
    [uuid, setModel, setFiles, saveFiles, dispatch, mounted],
  );

  const handleNotifyDialogClose = useCallback(async () => {
    setOpenNotifyDialog(false);
    if (uuid) {
      await modelLoadDetail(dispatch, uuid);
      navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid}));
    }
  }, [dispatch, navigate, uuid]);

  const renderEditFooter = useCallback(
    ({
      onBack,
      onNext,
      disableNext,
      nextHtmlType,
      nextText,
    }: {
      onBack?: () => void;
      onNext?: () => void;
      disableNext?: boolean;
      nextHtmlType?: 'button' | 'submit' | 'reset';
      nextText: string;
    }) => (
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '16px 80px',
          borderTop: '1px solid #d9d9d9',
          background: '#fff',
          zIndex: 1000,
          gap: '12px',
        }}
      >
        <Button size="large" onClick={onBack || handleEditCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          size="large"
          disabled={!!disableNext}
          htmlType={nextHtmlType || 'button'}
          onClick={nextHtmlType === 'submit' ? undefined : onNext}
        >
          {nextText}
        </Button>
      </div>
    ),
    [handleEditCancel],
  );

  if (!initialized || !models) {
    return (
      <DefaultPage>
        <SuspenseNull />
      </DefaultPage>
    );
  }

  return (
    <DefaultPage ref={scrollRef}>
      <DialogNavigationIntercepted isEnabled={false} setEnabledInterceptorRef={setEnabledInterceptorRef} />

      <ModelUploadContext.Provider value={contextValue}>
        {!editMode ? (
          <ModelUploadHeaderRedesigned
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            getStepStatus={getStepStatusCb}
          />
        ) : (
          <div style={{borderBottom: '1px solid #e8e8e8', padding: '24px 32px 20px', background: '#ffffff'}}>
            <div style={{maxWidth: '1400px', margin: '0 auto'}}>
              <h1 style={{fontSize: '42px', fontWeight: 400, marginBottom: '8px', color: '#1f1f1f', lineHeight: 1.1}}>
                Edit Model Entry
              </h1>
              <div style={{fontSize: '24px', color: '#595959', lineHeight: 1.3}}>
                Edit model files, documentation and metadata
              </div>
            </div>
          </div>
        )}
        {activeStep === MODEL_UPLOAD_STEPS.FILES && (!editMode || loadedFiles) && (
          <StepFiles
            useRedesign={true}
            model={model}
            files={editMode ? loadedFiles! : files}
            onChange={(newModel, newFiles) => {
              setModel(newModel);
              setFiles(newFiles);
            }}
            onSubmit={(newModel, newFiles) => {
              if (editMode) {
                handleEditFilesSubmit(newModel, newFiles);
                return;
              }
              setModel(newModel);
              setFiles(newFiles);
              setActiveStep(MODEL_UPLOAD_STEPS.METADATA);
            }}
            onBack={() => {
              // Navigate back to model list or home
              navigate(getUrlRoute(MRAppRoutes.MODEL_LIST));
            }}
            buttons={
              editMode
                ? ({onNext}) =>
                    renderEditFooter({
                      onBack: handleEditCancel,
                      onNext,
                      nextText: 'Update Files',
                    })
                : undefined
            }
          />
        )}
        {activeStep === MODEL_UPLOAD_STEPS.METADATA && (
          <StepMetadata
            useRedesign={true}
            model={model}
            files={files}
            showErrors={showErrorsOnStep === MODEL_UPLOAD_STEPS.METADATA}
            onChange={setModel}
            onRevopsNameChange={setRevopsName}
            onStewardReset={handleStewardReset}
            onSubmit={(newModel) => {
              if (editMode) {
                handleEditMetadataSubmit(newModel);
                return;
              }
              setModel(newModel);
              // Skip LINEAGE step for redesign - it's included in Metadata
              setActiveStep(MODEL_UPLOAD_STEPS.PERMISSIONS);
            }}
            onBack={() => (editMode ? handleEditCancel() : setActiveStep(MODEL_UPLOAD_STEPS.FILES))}
            buttons={
              editMode
                ? ({disableNext, nextHtmlType}) =>
                    renderEditFooter({
                      onBack: handleEditCancel,
                      disableNext,
                      nextHtmlType,
                      nextText: 'Update Metadata',
                    })
                : undefined
            }
          />
        )}
        {activeStep === MODEL_UPLOAD_STEPS.LINEAGE && (
          <StepLineage
            model={model}
            files={files}
            onChange={setModel}
            onSubmit={(updatedModel: ModelDTO) => {
              setModel(updatedModel);
              setActiveStep(MODEL_UPLOAD_STEPS.PERMISSIONS);
            }}
          />
        )}
        {activeStep === MODEL_UPLOAD_STEPS.PERMISSIONS && (
          <StepPermissions
            useRedesign={true}
            permissions={{...permissions, model}}
            onChange={({model, ...newPermissions}) => {
              setPermissions(newPermissions);
              setModel(model);
            }}
            onSubmit={(data) => {
              if (editMode) {
                handleEditPermissionsSubmit(data);
                return;
              }
              const {model, ...newPermissions} = data;
              setPermissions(newPermissions);
              setModel(model);
              setActiveStep(MODEL_UPLOAD_STEPS.NAME_CONFIRMATION);
            }}
            onBack={() => (editMode ? handleEditCancel() : setActiveStep(MODEL_UPLOAD_STEPS.METADATA))}
            buttons={
              editMode
                ? ({nextHtmlType}) =>
                    renderEditFooter({
                      onBack: handleEditCancel,
                      nextHtmlType,
                      nextText: 'Update Permissions',
                    })
                : undefined
            }
          />
        )}
        {activeStep === MODEL_UPLOAD_STEPS.NAME_CONFIRMATION && (
          <StepReviewRedesigned
            model={model}
            revopsName={revopsName}
            permissions={{...permissions, model}}
            files={files}
            onSubmit={() => save(model, false)}
            onBack={() => setActiveStep(MODEL_UPLOAD_STEPS.PERMISSIONS)}
            aiSummaryOptedOut={aiSummaryOptedOut}
            onAiSummaryOptedOutChange={setAiSummaryOptedOut}
            buttons={({onBack, nextHtmlType}) => (
              <div
                style={{
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: '24px 80px',
                  borderTop: '1px solid #d9d9d9',
                  background: '#fff',
                  zIndex: 1000,
                  gap: '12px',
                }}
              >
                <Button size="large" onClick={onBack} style={{minWidth: '100px'}}>
                  Back
                </Button>
                <Button size="large" onClick={() => save(model, false)} style={{minWidth: '120px'}}>
                  Save as Draft
                </Button>
                <Button
                  type="primary"
                  size="large"
                  htmlType={nextHtmlType}
                  onClick={() => save(model, true)}
                  disabled={!isStaticMetadataValid(model) || files.length === 0 || isModelWeb(model)}
                  style={{minWidth: '150px'}}
                >
                  Save and Publish
                </Button>
              </div>
            )}
          />
        )}
        <DialogProgress
          open={!!submitText}
          title={editMode || uuid ? 'Updating model entry' : 'Setting up your new model entry'}
          text={submitText}
        />
        <NotificationConfirmationDialogAntd
          open={openNotifyDialog}
          title="Notify Model Users ?"
          text="You've modified files in the model entry. Do you want to notify users for this change ?"
          description="An email notification will be sent to HEDS Lead, Value and Implementation Lead, Model Viewers and other Model Owners who has permission for this model entry."
          label="file update"
          successMessage="Email notification of modified file sent to model users"
          uuid={uuid}
          type="New File"
          handleCancel={handleNotifyDialogClose}
        />
      </ModelUploadContext.Provider>
    </DefaultPage>
  );
};

export default ModelUpload;
